import React, { useState, useCallback, useEffect, useRef } from "react";
import { Spin, Drawer, notification, Pagination, Modal, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import KpiBuilderPanel from "./KpiBuilderPanel";
import KpiBuilderCoachMark from "./KpiBuilderCoachMark";
import { COACH_MARK_ANIM_MS, MOBILE_BUILDER_BREAKPOINT } from "./kpiBuilderCoachMarkUtils";
import KpiGroupSection from "./KpiGroupSection";
import AddMetricModal from "./AddMetricModal";
import EditKpiModal from "./EditKpiModal";
import useKpiBoard from "./useKpiBoard";
import styles from "./kpiView.module.scss";
import InfoPopup from "components/GlobalExecutiveDashboard/InfoPopup";
import AlertPopup from "components/GlobalExecutiveDashboard/Alert";
import AlertLimitUpgradeModal from "components/Alerts/AlertLimitUpgradeModal";
import { fetchAlertLimits, shouldOpenAlertUpgrade } from "util/alertLimits";
import { useCopilotShell } from "contexts/CopilotShellContext";
import RenameDeletePopup from "helperComponents/RenameDeletePopup";
import WelcomeDrawerReviewsList from "pages/Welcome/WelcomeDrawerReviewsList";
import {
  normalizeWelcomeDrawerReviewDoc,
  sortReviewsNewestFirst,
} from "pages/Welcome/welcomeDrawerReviewDoc";
import { useWelcomeReviewsDrawerCloseEffect } from "pages/Welcome/useWelcomeReviewsDrawerCloseEffect";
import {
  WELCOME_REVIEWS_DRAWER_SHARED_PROPS,
  welcomeReviewsDrawerWidth,
} from "pages/Welcome/welcomeReviewsDrawerProps";
import { apiRequest } from "util/services";
import {
  resolveKpiTitle,
  formatSparklineTooltipDate,
  parseKpiDashboardIds,
  resolveKpiPivotFilterForApi,
  resolveKpiAlertSourceForApi,
  resolveKpiAlertIdForApi,
  resolveKpiAlertMetricsForApi,
  buildKpiAlertCreatePlans,
  resolveKpiSubtopics,
  resolveKpiMetricReviewFilter,
  resolveKpiRollingDayRange,
  kpiCompareWindowDays,
  normalizeKpiCompareWindow,
  resolveKpiMetricFilterLabel,
  resolveSparklineBucketRange,
} from "./kpiViewUtils";
import {
  trackKpiCreated,
  trackKpiGroupAction,
  trackKpiViewAction,
} from "util/productAnalytics";

const REVIEWS_PAGE_SIZE = 10;

export default function KpiViewBoard({
  selectedTeamItem,
  teamList = [],
  chartPeriod,
  setDataLoading,
  readOnly = false,
  presentationMode = false,
  reviewsPresentationMode = false,
  focusKpiId = null,
}) {
  const { t } = useTranslation();
  const theme = useSelector((state) => state.DashboardReducer.theme);
  const { setCopilotOpen, hasCxGptAccess } = useCopilotShell();
  const [presentationPage, setPresentationPage] = useState(0);
  const [scrolledReviewsStatus, setScrolledReviewsStatus] = useState({});
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [alertLimits, setAlertLimits] = useState(null);
  const [builderCoachMarkOpen, setBuilderCoachMarkOpen] = useState(false);
  const [mobileBuilderOpen, setMobileBuilderOpen] = useState(false);
  const coachMarkTimerRef = useRef(null);
  const focusKpiAppliedRef = useRef(false);

  const handleOrgAddonUpgrade = useCallback(async () => {
    const limits = await fetchAlertLimits();
    setAlertLimits(limits);
    setShowUpgradeModal(true);
  }, []);

  const handleOpenAdvisorKpi = useCallback(() => {
    if (selectedTeamItem) {
      try {
        sessionStorage.setItem("copilotKpiTeamId", String(selectedTeamItem));
      } catch (_) {}
    }
    try {
      sessionStorage.setItem(
        "copilotPendingPrompt",
        t(
          "team_kpi_view.advisorKpiStarter",
          "I want to create a KPI by selecting a dashboard, pivot, and category"
        )
      );
    } catch (_) {}
    setCopilotOpen(true);
  }, [selectedTeamItem, setCopilotOpen, t]);

  const {
    loading,
    backgroundLoading,
    sourcesLoading,
    kpis,
    groups,
    sources,
    totalKpis,
    kpiOrgUsed,
    kpiMaxLimit,
    kpiLimitReached,
    addKpi,
    createKpisFromTopics,
    deleteKpi,
    renameKpi,
    moveKpi,
    editKpi,
    renameGroup,
    fetchBoard,
  } = useKpiBoard({
    selectedTeamItem,
    chartPeriod,
    setDataLoading,
    t,
    startIndex: presentationMode ? presentationPage : 0,
    numTopKpis: 4,
    progressiveLoad: !presentationMode && !reviewsPresentationMode,
    loadSources: !readOnly && !presentationMode,
    showReviews: reviewsPresentationMode,
    onOrgAddonUpgradeRequired: handleOrgAddonUpgrade,
  });

  useEffect(() => {
    setScrolledReviewsStatus({});
  }, [presentationPage, selectedTeamItem]);

  useEffect(() => {
    focusKpiAppliedRef.current = false;
  }, [focusKpiId, selectedTeamItem]);

  useEffect(() => {
    if (!focusKpiId || loading || focusKpiAppliedRef.current || !kpis.length) {
      return undefined;
    }
    const el = document.getElementById(`kpi-card-${focusKpiId}`);
    if (!el) {
      return undefined;
    }
    focusKpiAppliedRef.current = true;
    const timer = window.setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add(styles.kpiCardFocus);
      window.setTimeout(() => {
        el.classList.remove(styles.kpiCardFocus);
      }, 2400);
    }, 120);
    return () => window.clearTimeout(timer);
  }, [focusKpiId, loading, kpis]);

  useEffect(() => {
    if (readOnly || presentationMode) return undefined;
    fetchAlertLimits().then(setAlertLimits);
    return undefined;
  }, [readOnly, presentationMode, selectedTeamItem]);

  const handleReviewsScrolled = useCallback((kpiId, done) => {
    if (kpiId == null) return;
    setScrolledReviewsStatus((prev) => ({ ...prev, [kpiId]: Boolean(done) }));
  }, []);

  useEffect(() => {
    if (!reviewsPresentationMode || loading) return undefined;
    const visibleKpis = groups.flatMap((group) => group.kpis).filter(Boolean);
    if (visibleKpis.length === 0) return undefined;
    const allScrolled = visibleKpis.every((kpi) => scrolledReviewsStatus[kpi.id]);
    if (!allScrolled) return undefined;
    const pageCount = Math.max(1, Math.ceil(totalKpis / 4));
    const timer = window.setTimeout(() => {
      setPresentationPage((prev) => (prev + 1) % pageCount);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [
    reviewsPresentationMode,
    scrolledReviewsStatus,
    groups,
    loading,
    totalKpis,
  ]);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [presetMetric, setPresetMetric] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [infoItem, setInfoItem] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertItem, setAlertItem] = useState(null);
  const [alertMode, setAlertMode] = useState("add");
  const [alertSaving, setAlertSaving] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [renameItem, setRenameItem] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [showMove, setShowMove] = useState(false);
  const [moveItem, setMoveItem] = useState(null);
  const [moveTargetTeam, setMoveTargetTeam] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [reviewsItem, setReviewsItem] = useState(null);
  const [reviewsFilterDate, setReviewsFilterDate] = useState(null);
  const [reviewsMetricKey, setReviewsMetricKey] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  const [reviewsPage, setReviewsPage] = useState(0);

  const openAddModal = (metric = null) => {
    setPresetMetric(metric);
    setAddModalOpen(true);
  };

  const handleEditSubmit = async ({ item, metric, dashboardIds, pivots }) => {
    return editKpi({ item, metric, dashboardIds, pivots });
  };

  const handleAddSubmit = async ({ metric, dashboardIds }) => {
    return addKpi({ metric, dashboardIds });
  };

  const handleCreateKpi = async ({ topics, dashboards, pivots, mode = "combined" }) => {
    const ok = await createKpisFromTopics({
      topics,
      dashboardIds: dashboards,
      pivots,
      mode,
    });
    if (ok) {
      dismissBuilderChrome();
      trackKpiCreated({
        topicCount: topics?.length ?? 0,
        dashboardCount: dashboards?.length ?? 0,
        pivotCount: pivots?.length ?? 0,
        mode,
        teamId: selectedTeamItem,
      });
    }
    return ok;
  };

  const moveTargetTeams = teamList.filter(
    (team) =>
      team?.owned &&
      String(team.team_id) !== String(selectedTeamItem)
  );
  const canMoveKpi = !readOnly && moveTargetTeams.length > 0;

  const handleCardMenu = (action, item) => {
    const kpiTitle = resolveKpiTitle(item);
    const trackAction = `card_menu_${action}`;
    if (["info", "edit", "rename", "delete", "move"].includes(action)) {
      trackKpiViewAction(trackAction, {
        kpiId: item?.id,
        kpiTitle,
        teamId: selectedTeamItem,
      });
    }
    switch (action) {
      case "info":
        setInfoItem(item);
        setShowInfo(true);
        break;
      case "rename":
        setRenameItem(item);
        setRenameValue(resolveKpiTitle(item));
        setShowRename(true);
        break;
      case "edit":
        setEditItem(item);
        setShowEdit(true);
        break;
      case "delete":
        deleteKpi(item);
        break;
      case "move":
        setMoveItem(item);
        setMoveTargetTeam(moveTargetTeams[0]?.team_id ?? null);
        setShowMove(true);
        break;
      default:
        break;
    }
  };

  const fetchKpiReviews = useCallback(
    async (item, { date = null, startIndex = 0, metricKey = null } = {}) => {
      if (!item) return;
      setReviewsLoading(true);
      const dashboardIds = parseKpiDashboardIds(item);
      const metric = Array.isArray(item.metric)
        ? item.metric
        : item.metric != null
        ? [item.metric]
        : [];

      if (!dashboardIds.length || !metric.length) {
        setReviews([]);
        setReviewsTotal(0);
        setReviewsLoading(false);
        return;
      }

      const payload = {
        dashboard_ids: dashboardIds,
        metric,
        period: chartPeriod || "30D",
        page_size: REVIEWS_PAGE_SIZE,
        start_index: startIndex,
      };
      const day = date ? String(date).slice(0, 10) : null;
      if (day) {
        const trend = item?.kpi_v3?.trend;
        const gran = trend?.granularity;
        if (gran === "rolling" || gran === "weekly") {
          const bucketDays =
            Number(trend?.bucket_days) > 0
              ? Number(trend.bucket_days)
              : gran === "weekly"
                ? 7
                : 30;
          const range = resolveSparklineBucketRange(day, bucketDays);
          payload.since = range.since;
          payload.until = range.until;
        } else {
          payload.since = day;
          payload.until = day;
        }
      } else {
        const rolling = resolveKpiRollingDayRange(
          kpiCompareWindowDays(chartPeriod),
        );
        payload.since = rolling.since;
        payload.until = rolling.until;
      }
      const metricFilter = resolveKpiMetricReviewFilter(metricKey);
      if (metricFilter.sentiment) {
        payload.sentiment = metricFilter.sentiment;
      }
      if (metricFilter.intent) {
        payload.intent = metricFilter.intent;
      }
      const pivotFilter = resolveKpiPivotFilterForApi(item);
      if (pivotFilter) {
        payload.pivot_filter = pivotFilter;
      }

      const result = await apiRequest(
        "executive_summary/get_kpi_reviews",
        "POST",
        payload,
        true
      );

      if (result?.success) {
        const docs = sortReviewsNewestFirst(
          (result.success.docs || [])
            .map((row) => normalizeWelcomeDrawerReviewDoc(row))
            .filter(Boolean),
        );
        setReviews(docs);
        setReviewsTotal(result.success.total_num_docs || docs.length);
        setReviewsPage(startIndex);
      } else {
        notification.error({
          message: result?.error || t("team_kpi_view.fetch_reviews_failed", "Could not load reviews"),
        });
        setReviews([]);
        setReviewsTotal(0);
      }
      setReviewsLoading(false);
    },
    [chartPeriod]
  );

  const handleOpenReviews = useCallback(
    async (item, options = {}) => {
      const date = options?.date ? String(options.date).slice(0, 10) : null;
      const metricKey = options?.metricKey || null;
      trackKpiViewAction(date && !metricKey ? "sparkline_click" : "reviews_open", {
        kpiId: item?.id,
        kpiTitle: resolveKpiTitle(item),
        metricKey,
        date,
        teamId: selectedTeamItem,
      });
      setReviewsItem(item);
      setReviewsFilterDate(date);
      setReviewsMetricKey(metricKey);
      setReviewsOpen(true);
      setReviewsPage(0);
      await fetchKpiReviews(item, { date, startIndex: 0, metricKey });
    },
    [fetchKpiReviews, selectedTeamItem]
  );

  const handleReviewsPageChange = useCallback(
    (page) => {
      if (!reviewsItem) return;
      fetchKpiReviews(reviewsItem, {
        date: reviewsFilterDate,
        startIndex: Math.max(0, page - 1),
        metricKey: reviewsMetricKey,
      });
    },
    [fetchKpiReviews, reviewsFilterDate, reviewsItem, reviewsMetricKey]
  );

  const handleCloseReviews = useCallback(() => {
    setReviewsOpen(false);
    setReviewsFilterDate(null);
    setReviewsMetricKey(null);
    setReviewsPage(0);
  }, []);

  useWelcomeReviewsDrawerCloseEffect(reviewsOpen, handleCloseReviews);

  const reviewsDrawerTitle = reviewsItem
    ? [
        resolveKpiTitle(reviewsItem),
        reviewsMetricKey
          ? resolveKpiMetricFilterLabel(reviewsMetricKey, t)
          : null,
        reviewsFilterDate
          ? formatSparklineTooltipDate(reviewsFilterDate, {
              granularity: reviewsItem?.kpi_v3?.trend?.granularity || "daily",
              bucketDays: reviewsItem?.kpi_v3?.trend?.bucket_days,
            })
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  const handleConfigureAlert = (item, mode = "add") => {
    setAlertItem(item);
    setAlertMode(mode === "edit" ? "edit" : "add");
    setShowAlert(true);
  };

  const handleToggleAlert = async (item, enabled) => {
    if (enabled) {
      trackKpiViewAction("alert_on", {
        kpiId: item?.id,
        kpiTitle: resolveKpiTitle(item),
        teamId: selectedTeamItem,
      });
      handleConfigureAlert(item, item?.alerted ? "edit" : "add");
      return;
    }
    trackKpiViewAction("alert_off", {
      kpiId: item?.id,
      kpiTitle: resolveKpiTitle(item),
      teamId: selectedTeamItem,
    });
    const kpiId = resolveKpiAlertIdForApi(item);
    if (!kpiId) return;
    setAlertSaving(true);
    const response = await apiRequest(
      "notice/alerts/delete_for_kpi",
      "POST",
      { kpi_id: kpiId },
      true
    );
    setAlertSaving(false);
    if (response?.success) {
      notification.success({ message: t("notifications.146") });
      await fetchBoard();
    } else {
      notification.error({ message: response?.error });
    }
  };

  const handleSaveAlert = async (
    condition,
    threshold,
    users,
    frequency,
    smsBoxChecked,
    includeSubtopics = false,
    selectedSubtopicKeys = null,
  ) => {
    if (!alertItem) return;
    setAlertSaving(true);

    let response;
    let createdCount = 0;
    if (alertMode === "edit" && alertItem?.alert?.alert_id) {
      response = await apiRequest(
        "notice/alerts/edit",
        "POST",
        {
          alert_id: alertItem.alert.alert_id,
          condition,
          threshold: parseInt(threshold, 10),
          send_to: users,
          frequency,
          sms_checked: smsBoxChecked,
        },
        true
      );
    } else {
      const createPlans = buildKpiAlertCreatePlans(
        alertItem,
        includeSubtopics,
        includeSubtopics ? selectedSubtopicKeys : null,
      );
      if (!createPlans.length) {
        setAlertSaving(false);
        notification.error({ message: t("team_kpi_view.63") });
        return;
      }
      const limits =
        alertLimits ?? (await fetchAlertLimits().then((state) => state));
      if (limits && createPlans.length > (limits.remaining ?? 0)) {
        setAlertSaving(false);
        setAlertLimits(limits);
        setShowUpgradeModal(true);
        return;
      }
      response = null;
      for (const plan of createPlans) {
        response = await apiRequest(
          "notice/alerts/create",
          "POST",
          {
            team: String(selectedTeamItem),
            alert_title: plan.alert_title,
            metrics: plan.metrics,
            condition,
            threshold: parseInt(threshold, 10),
            source: plan.source,
            send_to: users,
            frequency,
            id: plan.id,
            sms_checked: smsBoxChecked,
          },
          true
        );
        if (!response?.success) {
          break;
        }
        createdCount += 1;
      }
    }

    setAlertSaving(false);
    if (response?.success) {
      trackKpiViewAction("alert_save", {
        kpiId: alertItem?.id,
        kpiTitle: resolveKpiTitle(alertItem),
        alertMode,
        teamId: selectedTeamItem,
        includeSubtopics: Boolean(includeSubtopics),
      });
      if (alertMode === "add" && includeSubtopics && createdCount > 1) {
        notification.success({
          message: t("team_kpi_view.alert_subtopics_created", {
            count: createdCount,
            defaultValue: "{{count}} alerts created (parent + subtopics).",
          }),
        });
      } else {
        notification.success({
          message:
            alertMode === "edit"
              ? t("notifications.145")
              : t("notifications.144"),
        });
      }
      setShowAlert(false);
      await fetchBoard();
      fetchAlertLimits().then(setAlertLimits);
    } else if (shouldOpenAlertUpgrade(response)) {
      const limits = await fetchAlertLimits();
      setAlertLimits(limits);
      setShowUpgradeModal(true);
    } else {
      notification.error({ message: response?.error });
    }
  };

  const handleDeleteGroup = useCallback(
    (group) => {
      trackKpiGroupAction("delete", {
        groupPage: group.page,
        groupName: group.name,
        teamId: selectedTeamItem,
      });
      renameGroup(group, "");
    },
    [renameGroup, selectedTeamItem],
  );

  const handleRenameGroup = useCallback(
    async (group, name) => {
      if (name && name !== group.name) {
        trackKpiGroupAction("rename", {
          groupPage: group.page,
          groupName: name,
          teamId: selectedTeamItem,
        });
      }
      return renameGroup(group, name);
    },
    [renameGroup, selectedTeamItem],
  );

  const closeBuilderCoachMark = useCallback(() => {
    setBuilderCoachMarkOpen(false);
  }, []);

  const dismissBuilderChrome = useCallback(() => {
    if (coachMarkTimerRef.current) {
      window.clearTimeout(coachMarkTimerRef.current);
      coachMarkTimerRef.current = null;
    }
    setBuilderCoachMarkOpen(false);
    setMobileBuilderOpen(false);
  }, []);

  const openBuilderCoachMark = useCallback(() => {
    setBuilderCoachMarkOpen(false);
    window.requestAnimationFrame(() => {
      setBuilderCoachMarkOpen(true);
    });
  }, []);

  const isMobileBuilderLayout = useCallback(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= MOBILE_BUILDER_BREAKPOINT;
  }, []);

  const revealBuilderCoachMark = useCallback(() => {
    const builder = document.getElementById("kpi-builder-panel");
    if (builder) {
      builder.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    if (coachMarkTimerRef.current) {
      window.clearTimeout(coachMarkTimerRef.current);
    }
    coachMarkTimerRef.current = window.setTimeout(() => {
      openBuilderCoachMark();
    }, COACH_MARK_ANIM_MS);
  }, [openBuilderCoachMark]);

  useEffect(
    () => () => {
      if (coachMarkTimerRef.current) {
        window.clearTimeout(coachMarkTimerRef.current);
      }
    },
    [],
  );

  const handleEmptySlotClick = useCallback(() => {
    if (isMobileBuilderLayout()) {
      if (!mobileBuilderOpen) {
        setMobileBuilderOpen(true);
        revealBuilderCoachMark();
        return;
      }
    }
    revealBuilderCoachMark();
  }, [isMobileBuilderLayout, mobileBuilderOpen, revealBuilderCoachMark]);

  const handleBuilderEngagement = useCallback(() => {
    if (builderCoachMarkOpen) {
      closeBuilderCoachMark();
    }
  }, [builderCoachMarkOpen, closeBuilderCoachMark]);

  const shellClass = presentationMode
    ? styles.pageShellPresentation
    : theme === "dark"
    ? `${styles.pageShell} ${styles.pageShellDark}`
    : styles.pageShell;

  const boardClass = [
    styles.board,
    readOnly ? styles.boardReadOnly : "",
    presentationMode ? styles.boardPresentation : "",
    mobileBuilderOpen ? styles.kpiBuilderMobileOpen : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={shellClass}>
      <div className={boardClass}>
        {!readOnly && !presentationMode && (
          <>
            <button
              type="button"
              className={styles.kpiBuilderMobileToggle}
              onClick={() => setMobileBuilderOpen((open) => !open)}
              aria-expanded={mobileBuilderOpen}
              aria-controls="kpi-builder-panel"
            >
              {t("team_kpi_view.builderMobileOpen", "Create KPI")}
            </button>
            <KpiBuilderPanel
              sources={sources}
              sourcesLoading={sourcesLoading}
              creating={loading && kpis.length === 0}
              kpiLimitReached={kpiLimitReached}
              onCreateKpi={handleCreateKpi}
              onBuilderEngagement={handleBuilderEngagement}
              onOpenAdvisor={handleOpenAdvisorKpi}
              showAdvisorButton={hasCxGptAccess}
              forceExpanded={builderCoachMarkOpen}
            />
          </>
        )}
        <main className={styles.main} aria-busy={loading}>
          {loading && kpis.length > 0 ? (
            <div className={styles.boardRefreshOverlay} role="status">
              <Spin />
              <span>
                {t("team_kpi_view.window_loading", "Updating trend…")}
              </span>
            </div>
          ) : null}
          {backgroundLoading ? (
            <div className={styles.boardRefreshBar} role="status" aria-live="polite">
              <Spin size="small" />
              <span>
                {t("team_kpi_view.65", "Loading more KPI cards…")} ({kpis.length}/
                {totalKpis})
              </span>
            </div>
          ) : null}
          <div className={styles.mainScroll}>
            {groups.map((group, index) => (
              <KpiGroupSection
                key={group.id}
                group={group}
                theme={theme}
                loading={loading}
                readOnly={readOnly || presentationMode}
                reviewsPresentationMode={reviewsPresentationMode}
                isFirstGroup={index === 0}
                onRenameGroup={
                  readOnly || presentationMode ? undefined : handleRenameGroup
                }
                onDeleteGroup={
                  readOnly || presentationMode ? undefined : handleDeleteGroup
                }
                onCardMenuAction={
                  readOnly || presentationMode ? undefined : handleCardMenu
                }
                showMoveAction={canMoveKpi}
                onOpenReviews={
                  reviewsPresentationMode ? undefined : handleOpenReviews
                }
                onReviewsScrolled={
                  reviewsPresentationMode ? handleReviewsScrolled : undefined
                }
                onToggleAlert={
                  readOnly || presentationMode ? undefined : handleToggleAlert
                }
                onConfigureAlert={
                  readOnly || presentationMode ? undefined : handleConfigureAlert
                }
                onEmptySlotClick={
                  readOnly || presentationMode ? undefined : handleEmptySlotClick
                }
                compareWindow={normalizeKpiCompareWindow(chartPeriod)}
                teamId={selectedTeamItem}
              />
            ))}
          </div>
        </main>
      </div>

      {!readOnly && !presentationMode && (
        <KpiBuilderCoachMark
          open={builderCoachMarkOpen}
          onClose={closeBuilderCoachMark}
        />
      )}

      {presentationMode && !reviewsPresentationMode && totalKpis > 0 && (
        <div className={styles.presentationPagination}>
          <Pagination
            simple
            responsive
            showSizeChanger={false}
            disabled={loading}
            current={presentationPage + 1}
            pageSize={4}
            total={totalKpis}
            onChange={(page) => setPresentationPage(page - 1)}
          />
        </div>
      )}

      {!readOnly && !presentationMode && (
        <>
      <AddMetricModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        sources={sources}
        presetMetric={presetMetric}
        onSubmit={handleAddSubmit}
        loading={loading}
      />

      <EditKpiModal
        open={showEdit}
        item={editItem}
        sources={sources}
        onClose={() => {
          setShowEdit(false);
          setEditItem(null);
        }}
        onSubmit={handleEditSubmit}
        loading={loading}
      />

      {showInfo && infoItem && (
        <InfoPopup
          data={infoItem}
          theme={theme}
          close={() => setShowInfo(false)}
        />
      )}

      {showAlert && alertItem && (
        <AlertPopup
          close={setShowAlert}
          handleAction={handleSaveAlert}
          theme={theme}
          type={alertMode}
          variant="anomaly"
          disable={alertSaving}
          editFrequency={alertItem.alert?.frequency}
          editUsers={alertItem.alert?.send_to}
          editCondition={alertItem.alert?.condition}
          editThreshold={alertItem.alert?.threshold}
          editSendSMS={alertItem.alert?.send_sms}
          subtopics={resolveKpiSubtopics(alertItem)}
          kpiTitle={resolveKpiTitle(alertItem)}
          alertLimitsRemaining={alertLimits?.remaining ?? null}
          alertLimitsMax={alertLimits?.max_allowed ?? null}
        />
      )}

      <AlertLimitUpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        limits={alertLimits}
        returnPath={window.location.pathname}
        source="kpi_view_limit"
      />

      {showRename && renameItem && (
        <RenameDeletePopup
          type="rename"
          closePopup={setShowRename}
          label={t("team_kpi_view.17")}
          text={renameValue}
          setText={setRenameValue}
          handleAction={() => {
            renameKpi(renameItem, renameValue);
            setShowRename(false);
          }}
        />
      )}

      <Modal
        open={showMove}
        title={t("team_kpi_view.83")}
        okText={t("team_kpi_view.83")}
        cancelText={t("button.cancel")}
        confirmLoading={loading}
        centered
        destroyOnClose
        onCancel={() => {
          setShowMove(false);
          setMoveItem(null);
          setMoveTargetTeam(null);
        }}
        onOk={async () => {
          if (!moveItem || !moveTargetTeam) return;
          const moved = await moveKpi(moveItem, moveTargetTeam);
          if (moved) {
            setShowMove(false);
            setMoveItem(null);
            setMoveTargetTeam(null);
          }
        }}
      >
        <p className={styles.moveKpiHint}>{t("team_kpi_view.85", { name: resolveKpiTitle(moveItem) })}</p>
        <label className={styles.moveKpiLabel} htmlFor="kpi-move-target-team">
          {t("team_kpi_view.82")}
        </label>
        <Select
          id="kpi-move-target-team"
          className={styles.moveKpiSelect}
          value={moveTargetTeam ?? undefined}
          onChange={setMoveTargetTeam}
          style={{ width: "100%" }}
          placeholder={t("team_kpi_view.82")}
        >
          {moveTargetTeams.map((team) => (
            <Select.Option key={team.team_id} value={team.team_id}>
              {team.team_name}
            </Select.Option>
          ))}
        </Select>
      </Modal>
        </>
      )}

      <Drawer
        {...WELCOME_REVIEWS_DRAWER_SHARED_PROPS}
        title={reviewsDrawerTitle}
        width={welcomeReviewsDrawerWidth()}
        open={reviewsOpen}
        onClose={handleCloseReviews}
        maskClosable={!reviewsLoading}
        extra={
          <button
            type="button"
            className="welcome-reviews-drawer-header-close"
            onClick={handleCloseReviews}
          >
            {t("common.close", "Close")}
          </button>
        }
      >
        <Spin spinning={reviewsLoading}>
          <WelcomeDrawerReviewsList
            allReviews={reviews}
            reviewsLoading={reviewsLoading}
            serverSide
            total={reviewsTotal}
            currentPage={reviewsPage + 1}
            onServerPageChange={handleReviewsPageChange}
            pageSize={REVIEWS_PAGE_SIZE}
            showTotalCount
            theme={theme}
          />
        </Spin>
      </Drawer>
    </div>
  );
}
